/* eslint-disable react-hooks/exhaustive-deps */
import {
  parseAsArrayOf,
  parseAsJson,
  parseAsString,
  parseAsStringEnum,
  useQueryState,
} from 'nuqs';
import { useCallback, useMemo } from 'react';
import { create } from 'zustand';
import { CreatorGender } from '~/clientApi/creator.model';
import { CategoryTag as Vertical } from '~/components/CategoryTag/CategoryTag.model';
import { FEATURE_FLAGS } from '~/constants';
import { useFeature } from '~/hooks/useFeatureFlag/useFeature';
import {
  CreatorLocationDetail,
  ILocation,
  LocationFilter,
} from '~/screens/MyAgency/components/SearchLocation';
import { ICreatorLocation } from '~/types';

type ZustandState = {
  isCreatorsFiltersDrawerOpen: boolean;
  openCreatorsFiltersDrawer: () => void;
  closeCreatorsFiltersDrawer: () => void;
};

const useZustandState = create<ZustandState>((set) => ({
  isCreatorsFiltersDrawerOpen: false,
  openCreatorsFiltersDrawer: () =>
    set(() => ({ isCreatorsFiltersDrawerOpen: true })),
  closeCreatorsFiltersDrawer: () =>
    set(() => ({ isCreatorsFiltersDrawerOpen: false })),
}));

export const useCreatorsFiltersStore = () => {
  const zustandState = useZustandState();
  const { isEnabled: isFoamTalentLocationEnabled } = useFeature(
    FEATURE_FLAGS.foamTalentLocation,
  );

  const [creatorGender, setCreatorGender] = useQueryState(
    'creatorGender',
    parseAsStringEnum<CreatorGender>(['Male', 'Female', 'Non Binary']),
  );

  const [creatorAge, setCreatorAge] = useQueryState('creatorAge');
  const [creatorLocation, setCreatorLocation] = useQueryState(
    'creatorLocation',
    parseAsJson<LocationFilter | null>(),
  );
  const [creatorVerticals, setCreatorVerticals] = useQueryState(
    'creatorVerticals',
    parseAsArrayOf(parseAsString).withDefault([]),
  );

  const setVerticals = useCallback((verticalsList: Vertical[]) => {
    setCreatorVerticals(verticalsList.length > 0 ? verticalsList : null);
  }, []);

  const setGender = useCallback((gender: CreatorGender | null) => {
    setCreatorGender(gender);
  }, []);

  const setAge = useCallback((age: string | null) => {
    setCreatorAge(age);
  }, []);

  const setLocation = useCallback((location: ICreatorLocation) => {
    if (!location) return setCreatorLocation(null);
    if (!isFoamTalentLocationEnabled)
      return setCreatorLocation(location as LocationFilter);

    const { city, country } = location as ILocation;
    // with FF foam_talent_location both location structures are supported, this TS assertion wont be necessary after removing the FF
    const regionName = (location as CreatorLocationDetail)?.regionName;
    const regionCode = (location as CreatorLocationDetail)?.regionCode;
    const countryCodeAlpha3 = (location as CreatorLocationDetail)
      ?.countryCodeAlpha3;

    setCreatorLocation({
      ...(city && { city }),
      ...(city && { regionCode }),
      ...(regionName && { regionName }),
      ...((city || regionName) && { countryCodeAlpha3 }),
      ...(country && { country }),
    } as LocationFilter);
  }, []);

  const clearFilter = useCallback(
    (
      filterType: 'verticals' | 'gender' | 'platform' | 'age' | 'location',
      value?: string,
    ) => {
      if (filterType === 'verticals') {
        const newVerticals = creatorVerticals.filter(
          (v: Vertical) => v !== value,
        );
        setVerticals(newVerticals as Vertical[]);
        return;
      }

      if (filterType === 'gender') {
        setGender(null);
        return;
      }

      if (filterType === 'age') {
        setAge(null);
        return;
      }

      if (filterType === 'location') {
        setLocation(null);
        return;
      }
    },
    [creatorVerticals.length],
  );

  const clearAllFilters = useCallback(async () => {
    setCreatorAge(null);
    setCreatorGender(null);
    setCreatorLocation(null);
    setCreatorVerticals(null);
  }, []);

  const locationAppliedFilter = useMemo(() => {
    if (!creatorLocation) return null;

    if (!isFoamTalentLocationEnabled)
      return [`location:${creatorLocation.city}`];

    return [
      creatorLocation?.city && `city:${creatorLocation.city}`,
      creatorLocation?.regionName && `region:${creatorLocation.regionName}`,
      creatorLocation?.country && `country:${creatorLocation.country}`,
    ];
  }, [isFoamTalentLocationEnabled, creatorLocation]);

  const locationAppliedFilterLabel = useMemo(() => {
    if (!creatorLocation) return null;

    if (!isFoamTalentLocationEnabled) return creatorLocation?.city || null;

    const { city, country, regionCode, regionName, countryCodeAlpha3 } =
      creatorLocation;
    if (!city && !regionName && !country) return null;

    return [
      city,
      city ? regionCode : regionName,
      city || regionName ? countryCodeAlpha3 : country,
    ]
      .filter(Boolean)
      .join(', ');
  }, [isFoamTalentLocationEnabled, creatorLocation]);

  const appliedFilters = useMemo(() => {
    let applied = [];

    if (creatorVerticals.length) {
      applied.push(creatorVerticals.map((v) => `category:${v}`));
    }
    if (creatorGender) applied.push(`gender:${creatorGender}`);

    if (creatorAge) applied.push(`age:${creatorAge}`);

    if (locationAppliedFilter) applied.push(locationAppliedFilter);

    return applied.flat();
  }, [creatorVerticals, creatorGender, creatorAge, creatorLocation]);

  return {
    ...zustandState,
    verticals: creatorVerticals,
    setVerticals,
    gender: creatorGender,
    setGender,
    age: creatorAge,
    setAge,
    location: creatorLocation,
    locationAppliedFilter: locationAppliedFilterLabel,
    setLocation,
    creatorGender,
    creatorAge,
    creatorLocation,
    clearFilter,
    clearAllFilters,
    appliedFilters,
  };
};
